import React, { useState, useMemo } from 'react';
import { ResponsiveCirclePacking } from '@nivo/circle-packing'
import { Row, Switch } from "antd";
import { scaleOrdinal } from 'd3-scale';
import { schemeDark2, schemeAccent } from 'd3-scale-chromatic';
import { rgb } from 'd3-color';
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import {AutorenewRounded, EnergySavingsLeafRounded, PaletteRounded} from "@mui/icons-material";

const NivoCircleChart = ({ data, valueField = 'value', leavesOnly = true,
                         extraLeft, extraRight}) => {

    const [zoomedId, setZoomedId] = useState(null);
    const [onlyLeaves, setOnlyLeaves] = useState(leavesOnly);

    // Generate a color scale for sectors (depth 1)
    const colorScale = useMemo(() => {
        const sectors = data.children.map(child => child.name);
        return scaleOrdinal()
            .domain(sectors)
            .range(schemeDark2);
    }, [data]);

    // Custom color function
    const getColor = node => {

        if (node.depth === 1) {
            return colorScale(node.data.name);
        }
        if (node.depth === 2) {
            const parentColor = colorScale(node.path[1]); // Inherit from depth 1
            const newColor = rgb(parentColor).darker(0.5).toString(); // Darken the inherited color
            return newColor;
        }
        const baseColor = node.parent ? getColor(node.parent) : '#000000';
        return node.depth % 2 === 0 ? baseColor : rgb(baseColor).brighter(0.5).toString();
    };

    return (
        <>
            <Row justify={'end'} align={'middle'}>
                {extraLeft}
                <ButtonGroup>
                    <Button variant={'text'} startIcon={<AutorenewRounded/>} onClick={() => setZoomedId(null)}>Reorient</Button>
                    <Button variant={'text'} startIcon={<EnergySavingsLeafRounded/>} onClick={()=>setOnlyLeaves(!onlyLeaves)}>
                        {onlyLeaves ? "Show All Depths" : "Leaves Only"}
                    </Button>
                </ButtonGroup>
                {extraRight}
            </Row>
            <ResponsiveCirclePacking
                data={data}
                theme={{
                    axis: {
                        ticks: {
                            text: {
                                fontSize: 10,
                                fontWeight: '300',
                                fontFamily: "DM Sans, sans-serif",
                            }
                        }
                    },
                    labels: {
                        text: {
                            textAnchor: 'middle',
                            fontSize: 9,
                            fontWeight: '300',
                            fontFamily: "Inter, sans-serif",
                            dominantBaseline: 'central',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            textShadow: '1px 1px 2px rgba(0, 0, 0, 1)', // Add text shadow here


                        }
                    },
                    legends: {
                        text: {
                            fontWeight: '300',
                            fontFamily: "DM Sans, sans-serif",
                            fontSize: 10
                        },
                    },
                }}
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                id="name"
                value={valueField}
                colors={getColor}
                leavesOnly={onlyLeaves}
                childColor={{
                    from: 'color',
                    modifiers: [['darker', 0.3]]
                }}
                padding={0}
                enableLabels={true}
                labelsFilter={(n) => {
                    return n.node.depth === 2 && !n.label.includes('Removed')
                }}
                labelsSkipRadius={10}
                labelTextColor={'#fff'}
                borderWidth={0}
                borderColor={{
                    from: 'color',
                    modifiers: [['darker', 0.5]]
                }}
                defs={[
                    {
                    id: 'gradient1',
                    type: 'linearGradient',
                    colors: [
                        { offset: 0, color: '#fff' },
                        { offset: 100, color: '#fff' }
                    ]
                },

                    {
                        id: 'gradientDef',
                        type: 'linearGradient',
                        colors: [
                            { offset: 0, color: 'inherit', opacity: 0.2},
                            { offset: 100, color: 'inherit', opacity: 0.5 }
                        ]
                    },
                              {
                    id: 'red-gradient',
                    type: 'linearGradient',
                    colors: [
                        { offset: 0, color: 'rgba(255, 69, 69, 1)' },  // Lighter red
                        { offset: 100, color: 'rgba(139, 0, 0, 1)' }   // Darker red
                    ]
                },
                ]}
                fill={[
                    {
                        match: {
                            depth: 0
                        },
                        id: 'gradient1'
                    },
                    { match: {
                        depth: 2
                        }, id: 'gradientDef2' },
                    {
                        match: {
                            depth: 3  // Apply red gradient for depth 3
                        },
                        id: 'red-gradient'
                    },
                ]}
                zoomedId={zoomedId}
                motionConfig="slow"
                onClick={node => {
                    setZoomedId(zoomedId === node.id ? null : node.id);
                }}
                  tooltip={({ id, value, color }) => (
                      <div style={{background: 'rgba(255,255,255,0.5)', padding: '20px', backdropFilter: 'blur(10px)'}}>
                          <strong style={{ color }}>
                          {id.toUpperCase()}
                        </strong>
                      </div>

                  )}

            />
        </>
    )
}

export default NivoCircleChart;